export const DOCUMENT_HISTORY_SOURCE_BATCH_SIZE = 50;

export type DocumentHistoryAction = "added" | "edited" | "renamed" | "deleted" | "restored";

export type DocumentHistoryChangeField = "filename" | "categories" | "disciplines" | "stages";

export interface DocumentHistoryChange {
  field: DocumentHistoryChangeField;
  before: string[];
  after: string[];
}

export interface DocumentHistoryActor {
  id: number | null;
  name: string | null;
  avatarUrl: string | null;
  color: string | null;
}

export interface DocumentHistoryEntry {
  id: string;
  documentId: string;
  /** Reserved for a future upload-owned display name. `documents` has no such column today. */
  displayName: string | null;
  filename: string;
  filePath: string | null;
  contentType: string | null;
  size: number | null;
  version: number | null;
  versionGroup: string | null;
  isDeleted: boolean;
  state: string | null;
  action: DocumentHistoryAction;
  /** Before/after values for audit events whose payload carries a real change. */
  change?: DocumentHistoryChange;
  createdAt: string;
  actor: DocumentHistoryActor;
}

export interface HistoryProfileRow {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  avatar_url: string | null;
  color: string | null;
}

export interface DocumentHistorySourceCursor {
  offset: number;
  done: boolean;
  buffered: DocumentHistoryEntry[];
  /**
   * Pagination anchor: the `created_at` of the newest row this source has seen,
   * so later offset-based batches keep addressing the same result set even as
   * new rows arrive. Null until the first batch returns — the anchor is taken
   * from server-generated data rather than the browser clock, which on a laptop
   * that just woke can run behind the database and hide the newest rows.
   */
  snapshotAt: string | null;
  /**
   * The `created_at` of the OLDEST row this source has fetched so far. Every
   * source pages newest-first, so nothing it has left to return is newer than
   * this — which is what lets the merge emit another source's entries while this
   * one is still unresolved (see `takeNextEntry`). Taken from the raw rows, not
   * the entries they produced: a batch whose rows were all filtered out still
   * proves how far back this source has read. Null until the first batch.
   */
  watermark: string | null;
}

export interface DocumentHistoryCursor {
  documents: DocumentHistorySourceCursor;
  audit: DocumentHistorySourceCursor;
}

export interface DocumentHistoryPage {
  entries: DocumentHistoryEntry[];
  nextCursor?: DocumentHistoryCursor;
}

export interface DocumentHistoryScope {
  buildingId: number;
  orgId: string;
  projectId: number | null;
}

/**
 * The pagination anchor a source carries into its next batch: whatever it
 * already anchored on, else the newest row this batch returned.
 */
export function resolveSnapshotAt(
  cursor: DocumentHistorySourceCursor,
  rows: Array<{ created_at: string }>
): string | null {
  return cursor.snapshotAt ?? rows[0]?.created_at ?? null;
}

/** How far back this source has now read: the oldest row of its latest batch. */
export function resolveWatermark(
  cursor: DocumentHistorySourceCursor,
  rows: Array<{ created_at: string }>
): string | null {
  return rows[rows.length - 1]?.created_at ?? cursor.watermark;
}

export function actorFromProfile(
  profile: HistoryProfileRow | null,
  fallbackId: number
): DocumentHistoryActor {
  if (!profile) {
    return { id: fallbackId, name: null, avatarUrl: null, color: null };
  }

  const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(" ").trim();
  return {
    id: profile.id,
    name: fullName || profile.email,
    avatarUrl: profile.avatar_url,
    color: profile.color,
  };
}

function historyTimestamp(entry: DocumentHistoryEntry): number {
  const timestamp = new Date(entry.createdAt).getTime();
  return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp;
}

/** Newest-first ordering with a stable id tie-breaker for equal timestamps. */
export function compareDocumentHistoryEntries(
  left: DocumentHistoryEntry,
  right: DocumentHistoryEntry
): number {
  // Two unparseable timestamps subtract to NaN, which would make the whole sort
  // implementation-defined — fall through to the id tie-breaker instead.
  const timestampDifference = historyTimestamp(right) - historyTimestamp(left);
  if (timestampDifference !== 0 && !Number.isNaN(timestampDifference)) {
    return timestampDifference;
  }
  return right.id.localeCompare(left.id, undefined, { numeric: true });
}

/**
 * True when `entry` is newer than every row a source stopped at `watermark` can
 * still return, so emitting it now cannot leave that source's remaining entries
 * stranded above it on a later page. An equal timestamp is NOT newer: the id
 * tie-breaker could still place a row that source has yet to fetch above this
 * one. An unparseable timestamp on either side answers false — those sort last
 * anyway, so there is nothing to gain by racing them out early.
 */
export function isNewerThanWatermark(
  entry: DocumentHistoryEntry,
  watermark: string | null
): boolean {
  if (watermark === null) return false;
  const bound = new Date(watermark).getTime();
  const at = historyTimestamp(entry);
  return Number.isFinite(bound) && Number.isFinite(at) && at > bound;
}
